import useTranslation from 'next-translate/useTranslation';
import { Box, Skeleton } from '@mantine/core';
import { format } from 'date-fns';
import { match } from 'ts-pattern';

import { CurrencyConvertor } from '~/components/utils/formatters';
import { Button } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';

import type { LoyaltyTier } from '../../../../types/gamification.types';
import { TIER_COLORS } from '../../constants';
import dt from '../../testing.const';
import type { TierStatus } from '../../types/whalersClub.types';
import { formatPeriodDeadline } from '../../utils/formatPeriodDeadline';
import { getTierRewardPct } from '../../utils/getTierRewardPct';
import WhalersClubPromoLogo from '../WhalersClubPromoLogo';

import { useStyles } from './WhalersClubPromoCard.styles';

interface WhalersClubPromoCardProps {
  onDismiss?: () => void;
  onClick?: () => void;
  tiers: LoyaltyTier[];
  tierStatus: TierStatus;
  isLoading?: boolean;
}

function WhalersClubPromoCard({
  onDismiss,
  onClick,
  tierStatus,
  tiers,
  isLoading,
}: WhalersClubPromoCardProps) {
  const { t } = useTranslation('loyalty');

  const {
    rewardBasis,
    currentTierName,
    currentTierIndex,
    currentProgressAmount,
    currentRewardAmount,
    currentTierRewardPct,
    currentProgressPct,
    periodEndAt,
    nextTierRemainingAmount,
    isLastTier,
  } = tierStatus;
  const hasProgress = match(rewardBasis)
    .with('handle', () => currentProgressAmount >= 100)
    .with('net_loss', () => currentProgressAmount > 0)
    .exhaustive();
  const firstTierRewardPct = getTierRewardPct(tiers[0]);
  const day = match(rewardBasis)
    .with('handle', () => format(periodEndAt, 'EEEE'))
    .with('net_loss', () => formatPeriodDeadline(periodEndAt, 'EEEE'))
    .exhaustive();

  // Specific color override for the initial tier once the progress is started
  const { primary, background, progressGradient } = !currentTierName && hasProgress
    ? TIER_COLORS.Bronze
    : TIER_COLORS[currentTierName] || TIER_COLORS.Default;

  const { classes, cx } = useStyles({ primary, background });

  const progressSubtitle = hasProgress
    ? t('whalersClub.promoCard.subtitleTierAchieved', { day })
    : t('whalersClub.promoCard.subtitleBase');

  const basisCopy = match(rewardBasis)
    .with('handle', () => ({
      titleBase: t('whalersClub.promoCard.titleBase'),
      titleTierAchieved: t('whalersClub.promoCard.titleTierAchieved', { percent: currentTierRewardPct }),
      subtitleTierAchieved: progressSubtitle,
      tooltipReward: t('whalersClub.promoCard.tooltipReward', { percent: firstTierRewardPct }),
      reward: CurrencyConvertor(currentRewardAmount, true),
    }))
    .with('net_loss', () => ({
      titleBase: t('whalersClub.promoCard.titleBaseLossBack'),
      titleTierAchieved: t('whalersClub.promoCard.titleTierAchievedLossBack', { tierName: currentTierName }),
      subtitleTierAchieved: t('whalersClub.promoCard.subtitleTierAchievedLossBack', {
        percent: currentTierRewardPct, day
      }),
      tooltipReward: t('whalersClub.promoCard.tooltipRewardLossBack', { percent: firstTierRewardPct }),
      reward: t('whalersClub.promoCard.rewardPercent', { percent: currentTierRewardPct }),
    }))
    .exhaustive();

  // eslint-disable-next-line no-nested-ternary
  const title = currentTierName
    ? basisCopy.titleTierAchieved
    : hasProgress
      ? t('whalersClub.promoCard.titleTierNotAchieved', {
        amount: CurrencyConvertor(nextTierRemainingAmount), tierName: tiers[0].name
      })
      : basisCopy.titleBase;

  const subtitle = currentTierName ? basisCopy.subtitleTierAchieved : progressSubtitle;

  const handleDismiss = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onDismiss?.();
  };

  if (isLoading) {
    return <Skeleton className={classes.skeleton} />;
  }

  return (
    <Box
      className={cx(classes.container, [isLastTier && 'isDark', !(currentTierIndex >=0) && 'hasAnimation'])}
      data-test-id={dt.promoCard.container}
      onClick={onClick}
    >
      <WhalersClubPromoLogo tierIndex={currentTierIndex} hasProgress={hasProgress} />

      <div className={classes.textSection}>
        <Text variant="subhead-small" className={classes.title}>{title}</Text>
        <Text variant="subhead-x-small" className={classes.subtitle}>{subtitle}</Text>
        {!currentTierName && hasProgress && (
          <div className={classes.progressBarContainer}>
            <div className={classes.progressBarTooltip}>
              <Text variant="subhead-x-small">{basisCopy.tooltipReward}</Text>
            </div>
            <div className={classes.progressBarFill} style={{ width: `${currentProgressPct}%` }} />
          </div>
        ) }
      </div>

      {currentTierName && (
        <Text variant="subhead-x-small" className={classes.pill}>{basisCopy.reward}</Text>
      )}

      {!currentProgressAmount && (
        onDismiss ? (
          <Button
            variant="text"
            size="small"
            onClick={handleDismiss}
            className={classes.button}
            data-test-id={dt.promoCard.dismissButton}
          >
            <Text variant="headline-x-small">
              {t('whalersClub.promoCard.dismissButton')}
            </Text>
          </Button>
        ) : (
          <Button
            variant="secondary-fill"
            size="small"
            className={classes.button}
            data-test-id={dt.promoCard.moreButton}
            style={{ background: progressGradient }}
          >
            <Text variant="headline-x-small" color="gray.8">
              {t('whalersClub.promoCard.moreButton')}
            </Text>
          </Button>
        )
      )}
    </Box>
  );
}

export default WhalersClubPromoCard;
